import { Button, Select } from '@/components/ui'
import { ReloadOutlined } from '@ant-design/icons'
import type { BadgePrinterOption } from '@/hooks/useBadgePrinterSelection'

type PrinterSelectorProps = {
  selectedPrinterUid?: string | null
  printerOptions?: BadgePrinterOption[]
  isLoadingPrinters?: boolean
  onPrinterChange?: (value: string) => void
  onRefresh?: () => void
  placeholder?: string
  disabled?: boolean
  setupRequired?: boolean
  setupMessage?: string
  emptyMessage?: string
}

export default function PrinterSelector({
  selectedPrinterUid,
  printerOptions = [],
  isLoadingPrinters = false,
  onPrinterChange,
  onRefresh,
  placeholder = 'Select agent printer',
  disabled = false,
  setupRequired = false,
  setupMessage,
  emptyMessage = 'Create or regenerate a pairing code in Badge Printing settings, then enter the server URL and code in CloudExpo Print Agent.',
}: PrinterSelectorProps) {
  const showSetupMessage = !isLoadingPrinters && (setupRequired || printerOptions.length === 0)
  const helperMessage = setupMessage || emptyMessage
  const selectDisabled = disabled || setupRequired || printerOptions.length === 0
  // Printer labels are long ("ZDesigner ZD621-203dpi ZPL (Copy 1) (Print Station 1)") and the
  // closed box has to fit its column, so surface the full name as a hover tooltip.
  const selectedPrinterLabel = printerOptions.find(
    (option) => option.value === selectedPrinterUid,
  )?.label

  return (
    // Container query, not a viewport breakpoint: this component is reused in a narrow
    // sidebar (Badge Design) and in full-width settings panels on the same screen size,
    // so the row must key off its own width. Under 24rem the Refresh button drops to its
    // own line and the select takes the full width instead of being squeezed beside it.
    <div className="@container">
      <div className="grid grid-cols-1 gap-2 @sm:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0" title={selectedPrinterLabel}>
          <Select
            value={selectedPrinterUid || undefined}
            placeholder={showSetupMessage ? 'Pair print station' : placeholder}
            options={printerOptions}
            onChange={(value) => onPrinterChange?.(value)}
            disabled={selectDisabled}
          />
        </div>
        <Button
          variant="tertiary"
          className="justify-self-start @sm:justify-self-auto"
          icon={<ReloadOutlined />}
          loading={isLoadingPrinters}
          onClick={onRefresh}
          disabled={disabled}
        >
          Refresh
        </Button>
        {showSetupMessage && (
          <div className="col-span-full rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {helperMessage}
          </div>
        )}
      </div>
    </div>
  )
}
